import classNames from "classnames";
import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import { usePageHeaderSlot } from "components/AppLayout/PageHeaderSlotContext";
import { useSelector } from "react-redux";
import { RootState } from "store/store";

type HeadersProps = {
  right?: ReactNode;
  title?: string | ReactNode;
  left?: ReactNode;
  bottom?: ReactNode;
  className?: string;
  breadcrumbs?: {
    name: ReactNode;
    link?: string;
  }[];
  v2?: boolean;
};

export const Header = ({
  v2,
  right,
  title,
  left,
  bottom,
  className,
  breadcrumbs = [],
}: HeadersProps) => {
  const { slot, register } = usePageHeaderSlot();
  const pageBreadcrumb = useSelector(
    (state: RootState) => state.appLayout.breadcrumb
  );

  const shouldPortal = Boolean(v2 && slot && !pageBreadcrumb);

  useEffect(() => {
    if (shouldPortal) {
      return register();
    }
  }, [shouldPortal, register]);

  const breadcrumbsBlock = Boolean(breadcrumbs.length) && (
    <Breadcrumb.Root>
      {breadcrumbs.map((e) => (
        <Breadcrumb.Item link={e.link} key={e.link}>
          {e.name}
        </Breadcrumb.Item>
      ))}
    </Breadcrumb.Root>
  );

  const titleRow = (
    <div className="t-flex t-w-full t-items-center t-justify-between t-gap-2 sm:t-flex-wrap">
      <div className="t-flex t-flex-1 t-items-center t-justify-between t-gap-2 t-text-i-primary-100">
        {title && (
          <h5
            className="!t-m-0 t-text-subtitle-lg t-font-medium t-leading-6 t-text-text-60"
            data-testid="header-title"
          >
            {title}
          </h5>
        )}
        {left}
      </div>
      {right}
    </div>
  );

  if (shouldPortal && slot) {
    return (
      <>
        {createPortal(
          <div className="t-flex t-w-full t-flex-col t-gap-1">
            {breadcrumbsBlock}
            {titleRow}
          </div>,
          slot
        )}
        {bottom}
      </>
    );
  }

  if (v2) {
    return (
      <>
        <div className="t-flex t-w-full t-flex-col t-gap-1 t-py-4">
          {breadcrumbsBlock}
          {titleRow}
        </div>
        {bottom}
      </>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-2 t-bg-surface">
      {Boolean(breadcrumbs.length) && (
        <div className={classNames({ "t-px-6 t-pt-4": !v2 })}>
          {breadcrumbsBlock}
        </div>
      )}
      <div
        className={classNames(
          `t-sticky t-flex t-flex-col t-top-0 t-z-header t-gap-1 t-border-neutral-0 sm:t-flex-wrap t-bg-surface`,
          className || "",
          {
            "t-pb-0": Boolean(bottom),
            "t-px-6 t-py-4": !v2,
            "t-border-x-0 t-border-b t-border-t-0 t-border-solid":
              !v2 || bottom,
          }
        )}
      >
        {titleRow}
        {bottom}
      </div>
    </div>
  );
};
